import dayjs from "dayjs";

/**
 * One day's attendance, classified for the printed report (2026-09-17).
 *
 * "Late" has no server-side definition: a duty slot carries no start time of its
 * own, and the only configured times are the global shift changeovers (Day 06:00,
 * Night 18:00). Office staff do not start at 06:00, so the reporting time is the
 * reader's to set on the print screen - it defaults to the shift start and is
 * remembered per shift in this browser.
 */

export const DEFAULT_REPORTING_TIME = { Day: "06:00", Night: "18:00" };

export const STATUS = {
  ON_TIME: "On time",
  LATE: "Late",
  NOT_MARKED: "Not marked",
};

const storageKey = (shift) => `attendanceDayReport.reporting.${shift}`;

export const loadReportingSettings = (shift) => {
  const fallback = { time: DEFAULT_REPORTING_TIME[shift] || "06:00", graceMinutes: 0 };
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey(shift)) || "null");
    return saved && /^\d{2}:\d{2}$/.test(saved.time) ? { ...fallback, ...saved } : fallback;
  } catch {
    return fallback;
  }
};

export const saveReportingSettings = (shift, settings) => {
  try {
    window.localStorage.setItem(storageKey(shift), JSON.stringify(settings));
  } catch {
    // A blocked store only means the time is not remembered next time.
  }
};

// Minutes after (reporting time + grace) on the attendance date. A night-shift
// punch after midnight is simply further past 18:00, so no special case is needed.
export const lateMinutesOf = (capturedDate, attendanceDate, reporting) => {
  if (!capturedDate || !attendanceDate || !reporting?.time) return 0;
  const [h, m] = reporting.time.split(":").map(Number);
  const due = dayjs(attendanceDate).hour(h).minute(m).second(0).add(Number(reporting.graceMinutes) || 0, "minute");
  const diff = dayjs(capturedDate).diff(due, "minute");
  return diff > 0 ? diff : 0;
};

export const formatLate = (minutes) => {
  if (minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

export const classifyRows = (rows, attendanceDate, reporting) =>
  rows.map((row) => {
    if (!row.isMarked) return { ...row, status: STATUS.NOT_MARKED, lateMinutes: 0 };
    const lateMinutes = lateMinutesOf(row.capturedDate, attendanceDate, reporting);
    return { ...row, status: lateMinutes > 0 ? STATUS.LATE : STATUS.ON_TIME, lateMinutes };
  });

export const summarize = (classified) => ({
  total: classified.length,
  marked: classified.filter((r) => r.isMarked).length,
  onTime: classified.filter((r) => r.status === STATUS.ON_TIME).length,
  late: classified.filter((r) => r.status === STATUS.LATE).length,
  notMarked: classified.filter((r) => r.status === STATUS.NOT_MARKED).length,
});

// Customer -> slots, customers alphabetically, slots in the order the server sent.
export const groupByCustomer = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = `${row.branchId}-${row.custId}`;
    if (!groups.has(key)) groups.set(key, { key, custName: row.custName || "Unknown Customer", area: row.area, rows: [] });
    groups.get(key).rows.push(row);
  });
  return [...groups.values()].sort((a, b) => a.custName.localeCompare(b.custName));
};
